import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Button, EmptyState, Skeleton } from '@/components';
import { WorkspaceViewsPage } from '@/components/workspace/WorkspaceViewsPage';
import { workspaceBoardsQueryOptions } from '@/lib/boards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { signInHref, useSession } from '@/lib/session';
import {
  useCreateWorkspaceView,
  workspaceViewsQueryOptions,
} from '@/lib/workspace-views';
import { workspaceQueryOptions } from '@/lib/workspaces';

export const Route = createFileRoute('/w/$workspaceId/views')({
  component: WorkspaceViewsRoute,
});

function WorkspaceViewsRoute() {
  const { t } = useInterfaceLanguage();
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const {
    profile,
    isPending: sessionPending,
    isSignedOut,
    error: sessionError,
  } = useSession();
  const canFetch = !isSignedOut && !sessionPending && profile !== null;
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const boards = useQuery({
    ...workspaceBoardsQueryOptions(workspaceId, 'open'),
    enabled: canFetch,
  });
  const views = useQuery({
    ...workspaceViewsQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const createView = useCreateWorkspaceView(workspaceId);

  if (isSignedOut) {
    return (
      <WorkspaceViewsFrame title={t('workspaceViews.index.title')}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.index.signInTitle')}
          body={t('workspaceViews.index.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({ pathname: `/w/${workspaceId}/views`, search: '' })}
            >
              {t('workspaceViews.index.signInAction')}
            </Button>
          }
        />
      </WorkspaceViewsFrame>
    );
  }

  if (sessionPending) {
    return (
      <WorkspaceViewsFrame title={t('workspaceViews.index.title')}>
        <div aria-busy="true" className="flex flex-col gap-4">
          <Skeleton shape="block" count={3} />
        </div>
      </WorkspaceViewsFrame>
    );
  }

  if (profile === null) {
    return (
      <WorkspaceViewsFrame title={t('workspaceViews.index.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.index.accountErrorTitle')}
          body={sessionError?.message ?? t('workspaceViews.index.genericError')}
        />
      </WorkspaceViewsFrame>
    );
  }

  const loadError = workspace.error ?? boards.error ?? views.error;
  if (loadError !== null) {
    const notFound = loadError.code === 'not_found';
    return (
      <WorkspaceViewsFrame title={t('workspaceViews.index.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            /* ⚠ HYPHEN, matching the saved-view route. The table and calendar
               routes write the same sentence with an EM DASH, and both
               spellings are bound by tests this slice does not own. */
            notFound
              ? t('workspaceViews.index.notFound')
              : t('workspaceViews.index.loadFailedTitle')
          }
          {...(notFound ? {} : { body: loadError.message })}
        />
      </WorkspaceViewsFrame>
    );
  }

  return (
    <WorkspaceViewsPage
      workspaceName={workspace.data?.name ?? t('workspaceViews.index.workspaceFallback')}
      boards={boards.data ?? []}
      views={views.data?.views ?? []}
      loadingBoards={boards.isPending}
      loadingViews={views.isPending}
      createBusy={createView.isPending}
      createError={createView.error?.message}
      onCreate={(body) => {
        createView.mutate(body, {
          onSuccess: (view) => {
            void navigate({ href: `/workspace-views/${view.id}` });
          },
        });
      }}
      onRetry={() => {
        void workspace.refetch();
        void boards.refetch();
        void views.refetch();
      }}
    />
  );
}

function WorkspaceViewsFrame({
  children,
  title,
}: {
  readonly children: ReactNode;
  readonly title?: string | undefined;
}) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      {title === undefined ? null : <h1 className="sr-only">{title}</h1>}
      {children}
    </div>
  );
}
